import { Discount } from '@mui/icons-material';
import LoadingButton from '@mui/lab/LoadingButton';
import { Alert, Box, Button, Link as MUILink, Typography } from '@mui/material';
import Grid2 from '@mui/material/Unstable_Grid2';
import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Plan, ScenarioPlan, ScenarioPlanName } from '../api/gql';
import { getConfig } from '../utils/config';
import { getPriceWithCurrencyString } from '../utils/currencies';
import { formatPhoneNumber, formatPrice } from '../utils/format';
import { PlanCard } from './PlanCard';

const { salesPhone, salesEmail } = getConfig();
const TRANS_NS = 'billing';

type Props = {
  onPlanChoose: (plan: Plan) => void;
  standardPrice: number;
  scenarioPlan?: ScenarioPlan;
  standardDiscount?: number;
  contact?: {
    missingEmail?: boolean;
    missingPhone?: boolean;
  };
  isFreePlanDisplayed?: boolean;
  isFreePlanEnabled?: boolean;
  isLoading?: boolean;
  transNs?: string;
};

export const BillingPlans = ({
  scenarioPlan = { email: 3, sms: 3, call: 1, name: ScenarioPlanName.Plan_331 },
  transNs = TRANS_NS,
  isFreePlanDisplayed = true,
  ...props
}: Props) => {
  const { t } = useTranslation(transNs);
  return (
    <Box display={'flex'} flexDirection={'column'} alignItems={'center'}>
      <Grid2 container spacing={4} alignItems={'stretch'}>
        {isFreePlanDisplayed ? (
          <Grid2 xs={12} lg={4}>
            <PlanCard
              title={t('plan.first.title')}
              subtitle={t('plan.first.subtitle')}
              plansHeading={t('planIncludes')}
              noEmails={1}
              emailsDescription={t('plan.first.emailInfo')}
              noSms={1}
              smsDescription={t('plan.first.smsInfo')}
              noCalls={0}
              callsDescription={t('plan.first.phoneInfo')}
              endOfRemindingDescription={t('plan.first.endOfRemindingInfo')}
              disableSms={props.contact?.missingPhone}
              disableEmail={props.contact?.missingEmail}
              disableCalls={props.contact?.missingPhone}
              priceForUnit={getPriceWithCurrencyString(0, 'czk')}
              actionContent={
                <Box
                  display={'flex'}
                  flexDirection={'column'}
                  justifyContent={'center'}
                  alignItems={'center'}
                  width={'100%'}
                  py={2}
                >
                  <LoadingButton
                    loading={props.isLoading}
                    disabled={!props.isFreePlanEnabled}
                    size={'large'}
                    variant={'outlined'}
                    onClick={() => props.onPlanChoose(Plan.Trial)}
                  >
                    {props.isFreePlanEnabled ? t('plan.first.action') : t('plan.first.depleted')}
                  </LoadingButton>
                  <Typography mt={1} variant={'caption'} color={'textSecondary'}>
                    {t('plan.first.overline')}
                  </Typography>
                </Box>
              }
            />
          </Grid2>
        ) : null}
        <Grid2 xs={12} lg={isFreePlanDisplayed ? 4 : 6} sx={{ mt: { md: -4, xs: -2 }, mb: -2 }}>
          <PlanCard
            recommended
            title={t('plan.second.title')}
            subtitle={t('plan.second.subtitle')}
            plansHeading={t('planIncludes')}
            noEmails={scenarioPlan.email}
            emailsDescription={t('plan.second.emailInfo', {
              count: scenarioPlan.email,
            })}
            noSms={scenarioPlan.sms}
            smsDescription={t('plan.second.smsInfo', {
              count: scenarioPlan.sms,
            })}
            noCalls={scenarioPlan.call}
            callsDescription={t('plan.second.phoneInfo', {
              count: scenarioPlan.call,
            })}
            disableSms={props.contact?.missingPhone}
            disableEmail={props.contact?.missingEmail}
            disableCalls={props.contact?.missingPhone}
            endOfRemindingDescription={t('plan.second.endOfRemindingInfo')}
            priceForUnit={getPriceWithCurrencyString(99, 'czk')}
            actionContent={
              <Box
                flexDirection={'column'}
                display={'flex'}
                justifyContent={'center'}
                alignItems={'center'}
                width={'100%'}
                pt={2}
                px={2}
              >
                {props.standardDiscount ? (
                  <Alert icon={<Discount />} severity={'success'} sx={{ mb: 2 }}>
                    {props.contact?.missingPhone ? t('plan.second.missingPhoneNumberDiscount') : null}
                    {props.contact?.missingEmail ? t('plan.second.missingEmailDiscount') : null}
                    {!props.contact?.missingEmail && !props.contact?.missingPhone ? t('plan.second.discount') : null}
                  </Alert>
                ) : null}
                <LoadingButton
                  loading={props.isLoading}
                  size={'large'}
                  variant={'contained'}
                  onClick={() => props.onPlanChoose(Plan.Standard)}
                >
                  {t('plan.second.action', {
                    amount: formatPrice(props.standardPrice),
                  })}
                </LoadingButton>
                <Typography mt={1} variant={'caption'} color={'textSecondary'}>
                  {t('plan.second.overline')}
                </Typography>
              </Box>
            }
          />
        </Grid2>
        <Grid2 xs={12} lg={isFreePlanDisplayed ? 4 : 6}>
          <PlanCard
            title={t('plan.third.title')}
            subtitle={t('plan.third.subtitle')}
            plansHeading={t('planMayInclude')}
            noEmails={'...'}
            emailsDescription={t('plan.third.emailInfo')}
            noSms={'...'}
            smsDescription={t('plan.third.smsInfo')}
            noCalls={'...'}
            callsDescription={t('plan.third.phoneInfo')}
            endOfRemindingDescription={t('plan.third.endOfRemindingInfo')}
            priceForUnit={t('plan.basedOnAgreement')}
            actionContent={
              <Box
                flexDirection={'column'}
                display={'flex'}
                justifyContent={'center'}
                alignItems={'center'}
                width={'100%'}
                p={1}
              >
                <Box mb={2}>
                  <Typography sx={{ whiteSpace: 'break-spaces' }}>
                    <Trans
                      t={t}
                      i18nKey={'plan.third.contact'}
                      components={{
                        bold: <Typography variant={'subtitle1'} component={'span'} fontWeight={'bold'} />,
                        link1: <MUILink component={Link} to={`tel:${salesPhone}`} style={{ whiteSpace: 'nowrap' }} />,
                      }}
                      values={{
                        phoneNumber: formatPhoneNumber(salesPhone),
                      }}
                    />
                  </Typography>
                </Box>
                <Button
                  size={'large'}
                  variant={'outlined'}
                  component={Link}
                  target={'_blank'}
                  to={`mailto:${salesEmail}`}
                >
                  {t('plan.third.action')}
                </Button>
                <Typography mt={1} variant={'caption'} color={'textSecondary'}>
                  {t('plan.third.overline')}
                </Typography>
              </Box>
            }
          />{' '}
        </Grid2>
      </Grid2>
      <Box display={'flex'} mt={{ md: 6, xs: 2 }} justifyContent={'center'}>
        <Alert severity={'info'}>
          <Trans
            t={t}
            i18nKey={'alertInfo'}
            components={{
              link1: <MUILink component={Link} to={getConfig().planOfRemindingUrl} target={'_blank'} />,
            }}
          />
        </Alert>
      </Box>
    </Box>
  );
};
